import React from 'react'
import styled, { css, keyframes } from 'styled-components'

// Utils
import { useAppState } from '../context/AppContext'
import { createColor, DEFAULT_CULTURE_COLORS, hsla } from '../styles/colors'

interface IProps {
  className?: string
}

const SIZE = 64
// The rings share this much of the radius, leaving a small open centre
const RING_SPACE = 24

// One comet ring per culture value, every other ring turning the other way and
// each at its own pace. Only `transform` animates, so the browser runs it on the
// compositor and it stays smooth while the main thread is busy loading. Before a
// company is known (the sign-in page) it draws the default culture palette.
const Loader: React.FC<IProps> = ({ className }) => {
  const { company, theme } = useAppState()

  const values = company?.cultureValues ?? []
  const colors = values.length
    ? values.map((value) => {
        const hslArray = company?.companyColors[value.color]
        return hslArray
          ? createColor(hslArray)
          : theme.colors[value.color] ?? theme.colors.primary
      })
    : DEFAULT_CULTURE_COLORS.map((color) => createColor(color))
  const pitch = Math.min(6, RING_SPACE / colors.length)

  return (
    <Container className={className}>
      {colors.map((color, index) => (
        <Ring
          key={`loader-${index}`}
          $color={color}
          $inset={index * pitch}
          $thickness={pitch / 2}
          $duration={1 + index * 0.18}
        />
      ))}
    </Container>
  )
}

export default Loader

const spin = keyframes`
  to {
    transform: rotate(1turn);
  }
`

const spinBack = keyframes`
  to {
    transform: rotate(-1turn);
  }
`

const Container = styled.div`
  position: relative;
  flex-shrink: 0;
  width: ${SIZE}px;
  height: ${SIZE}px;
  margin: 0 auto;
`

// A comet over a faint track: the tail fades in from transparent up to the head
const comet = (color: string, stops: string) => css`
  background: conic-gradient(from 0turn, ${stops}), ${hsla(color, 0.1)};
`

const Ring = styled.span<{
  $color: string
  $inset: number
  $thickness: number
  $duration: number
}>`
  position: absolute;
  inset: ${({ $inset }) => $inset}px;
  border-radius: 50%;
  ${({ $color }) =>
    comet(
      $color,
      `transparent 0turn 0.3turn, ${$color} 0.75turn, transparent 0.75turn`
    )}
  /* Cut the disc down to a thin band */
  ${({ $thickness }) => {
    const band = `radial-gradient(farthest-side, transparent calc(100% - ${$thickness}px - 0.5px), #000 calc(100% - ${$thickness}px))`
    return css`
      -webkit-mask: ${band};
      mask: ${band};
    `
  }}
  animation: ${spin} ${({ $duration }) => $duration}s
    cubic-bezier(0.45, 0.05, 0.55, 0.95) infinite;

  /* The round head, where the comet ends (0.75turn = the left edge) */
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: ${({ $thickness }) => $thickness}px;
    height: ${({ $thickness }) => $thickness}px;
    border-radius: 50%;
    transform: translateY(-50%);
    background: ${({ $color }) => $color};
  }

  /* Mirrored comet: head on the right and the tail trailing clockwise, so it
     leads while turning back */
  &:nth-child(even) {
    ${({ $color }) =>
      comet(
        $color,
        `transparent 0turn 0.25turn, ${$color} 0.25turn, transparent 0.7turn`
      )}
    animation-name: ${spinBack};

    &::after {
      left: auto;
      right: 0;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 6s;
    animation-timing-function: linear;
  }
`
